import { useUpdateVehicleBaseDetailsMutation } from './use-update-vehicle-base-details.mutation';
import { useUpdateVehicleIdentificationMutation } from './use-update-vehicle-identification.mutation';
import { useUpdateVehicleInspectionDatesMutation } from './use-update-vehicle-inspection-dates.mutation';
import { useUpdateVehicleOdometerMutation } from './use-update-vehicle-odometer.mutation';
import { useVehicleImportMutation } from './use-vehicle-import.mutation';
import { VehicleClassType, VehicleInspectionType } from 'api-new/generated';
import { useState } from 'react';
import { toast } from 'react-toastify';

const API_CALL_DELAY_MS = 1000;

export type CdkFormData = {
  mileage?: number | null;
  regNo?: string | null;
  vin?: string | null;
  engineNumber?: string | null;
  class?: string | null;
  make?: string | null;
  model?: string | null;
  modelYear?: number | null;
  description?: string | null;
  variantCode?: string | null;
  dueDate?: string | null;
  dueOdometerReading?: number | null;
  lastDate?: string | null;
  lastOdometerReading?: number | null;
  lastMot?: string | null;
  motExpiry?: string | null;
};

export type UseCdkFormUpdateParams = {
  branchId: number;
  vehicleId: string;
  onSuccess?: (innerVehicleId?: number) => void;
};

export const useCdkFormUpdate = ({ branchId, vehicleId, onSuccess }: UseCdkFormUpdateParams) => {
  const { mutate: updateOdometer } = useUpdateVehicleOdometerMutation();
  const { mutate: updateIdentification } = useUpdateVehicleIdentificationMutation();
  const { mutate: updateBaseDetails } = useUpdateVehicleBaseDetailsMutation();
  // TODO: Uncomment when Service Data issue is fixed
  // const { mutate: updateServiceData } = useUpdateVehicleServiceDataMutation();
  const { mutate: updateInspectionDates } = useUpdateVehicleInspectionDatesMutation();
  const { mutate: vehicleImport } = useVehicleImportMutation();

  const [isUpdating, setIsUpdating] = useState(false);

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const updateCdkData = async (values: CdkFormData) => {
    setIsUpdating(true);

    // Update Odometer - always send in Miles
    if (values.mileage != null) {
      await new Promise<void>((resolve) => {
        updateOdometer(
          { branchId, vehicleId, mileageValue: values.mileage as number },
          {
            onSuccess: () => void toast.success('Odometer updated successfully', { autoClose: 10000 }),
            onError: () => void toast.error('Failed to update odometer', { autoClose: 10000 }),
            onSettled: () => resolve()
          }
        );
      });
      await delay(API_CALL_DELAY_MS);
    }

    const hasIdentificationToUpdate = values.regNo || values.vin;
    if (hasIdentificationToUpdate) {
      await new Promise<void>((resolve) => {
        updateIdentification(
          {
            branchId,
            vehicleId,
            identification: {
              licensePlate: values.regNo,
              vin: values.vin,
              engineNumber: values.engineNumber
            }
          },
          {
            onSuccess: () => void toast.success('Identification updated successfully', { autoClose: 10000 }),
            onError: () => void toast.error('Failed to update identification', { autoClose: 10000 }),
            onSettled: () => resolve()
          }
        );
      });
      await delay(API_CALL_DELAY_MS);
    }

    // Update Base Details - only if any editable field has value
    // Class, Make, Model are read-only, so we only update: modelYear, description, variant
    const hasBaseDetailsToUpdate = values.modelYear != null || values.description != null || values.variantCode != null;

    if (hasBaseDetailsToUpdate) {
      await new Promise<void>((resolve) => {
        updateBaseDetails(
          {
            branchId,
            vehicleId,
            description: {
              class: values.class ? (values.class as unknown as VehicleClassType) : undefined,
              make: values.make,
              model: values.model,
              modelYear: values.modelYear,
              description: values.description,
              variant: values.variantCode
            }
          },
          {
            onSuccess: () => void toast.success('Base details updated successfully', { autoClose: 10000 }),
            onError: () => void toast.error('Failed to update base details', { autoClose: 10000 }),
            onSettled: () => resolve()
          }
        );
      });
      await delay(API_CALL_DELAY_MS);
    }

    // Update Service Data - currently dates are not updating (issue not resolved)
    // TODO: Uncomment when the issue is fixed
    // const hasServiceDataToUpdate =
    //   values.dueDate != null ||
    //   values.dueOdometerReading != null ||
    //   values.lastDate != null ||
    //   values.lastOdometerReading != null;
    //
    // if (hasServiceDataToUpdate) {
    //   await new Promise<void>((resolve) => {
    //     updateServiceData(
    //       {
    //         branchId,
    //         vehicleId,
    //         serviceData: {
    //           dueDate: values.dueDate,
    //           dueOdometerReading: values.dueOdometerReading
    //             ? { unit: MileageUnit.MILES, value: values.dueOdometerReading }
    //             : undefined,
    //           lastDate: values.lastDate,
    //           lastOdometerReading: values.lastOdometerReading
    //             ? { unit: MileageUnit.MILES, value: values.lastOdometerReading }
    //             : undefined
    //         }
    //       },
    //       { onSettled: () => resolve() }
    //     );
    //   });
    //   await delay(API_CALL_DELAY_MS);
    // }

    // Update Inspection Dates (MOT) - only if both dates filled and motExpiry > today
    if (values.lastMot && values.motExpiry) {
      const motExpiryDate = new Date(values.motExpiry);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (motExpiryDate > today) {
        await new Promise<void>((resolve) => {
          updateInspectionDates(
            {
              branchId,
              vehicleId,
              type: VehicleInspectionType.GOVERNMENT,
              lastDate: values.lastMot,
              dueDate: values.motExpiry
            },
            {
              onSuccess: () => void toast.success('MOT dates updated successfully', { autoClose: 10000 }),
              onError: () => void toast.error('Failed to update MOT dates', { autoClose: 10000 }),
              onSettled: () => resolve()
            }
          );
        });
        await delay(API_CALL_DELAY_MS);
      }
    }

    // After all updates, call vehicle-import
    vehicleImport(
      { vehicleId },
      {
        onSuccess: (importedInnerVehicleId) => {
          toast.success('Vehicle import completed successfully', { autoClose: 10000 });
          setIsUpdating(false);
          onSuccess?.(importedInnerVehicleId);
        },
        onError: () => {
          toast.error('Failed to import vehicle', { autoClose: 10000 });
          setIsUpdating(false);
          onSuccess?.();
        }
      }
    );
  };

  return {
    updateCdkData,
    isUpdating
  };
};
